<template>
  <div>
    <NavigationBreadcrumb :items="breadcrumb_items"></NavigationBreadcrumb>
    <h3 class="mt-3">
      {{ $t("teaching.instructionalMethod.designTitle") }}
    </h3>

    <!-- E28 · La vista pasa a ser el lugar donde la institucion configura TODO lo
         del Diseño Instruccional, no solo los Modelos. Cada cosa en su pestaña. -->
    <b-tabs content-class="mt-3">
      <b-tab :title="$t('teaching.instructionalMethod.tabMethods')" active>
    <div style="display: flex" class="mx-3">
      <div class="col p-0" style="text-align: left">
        <ButtonMedium
          v-can="'teaching.add_instructionalmethod'"
          v-if="allows_crud"
          :tooltip_text="
            $t('reusable.common.addTooltip', {
              entity: $t('teaching.instructionalMethod.methodEntity'),
            })
          "
          :text_button="$t('reusable.common.add')"
          :click_button="() => $bvModal.show('new-instructional-method-modal')"
          :icon="'plus'"
          :variant="'primary'"
        >
        </ButtonMedium>
      </div>
    </div>

    <GenericBTable
      v-if="instructional_methods.length > 0"
      :items="instructional_methods"
      :fields="methodsFields"
      :filter="input_search"
      :filterCustom="filterCustom"
      @emitChangeSearchField="changeInputSearch"
      :show_pagination="true"
      :search_filter="true"
      :columns_display="true"
      :allows_crud="allows_crud"
      :display_id="display_id"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
    >
      <template #cell(moments)="row">
        <span
          v-for="moment in momentsByMethod(row.item.id)"
          :key="moment.id"
          class="moment-chip"
          :style="momentChipStyle(moment)"
        >
          {{ moment.name }}
        </span>
        <span v-if="momentsByMethod(row.item.id).length == 0" class="kl-hint">
          {{
            $tc("teaching.instructionalMethod.momentsCount", 0, { count: 0 })
          }}
        </span>
      </template>

      <template #cell(actions)="row">
        <ButtonSmall
          :tooltip_text="
            $t('teaching.instructionalMethod.manageMomentsTooltip')
          "
          :click_button="() => openMomentsModal(row.item)"
          :class="`mr-1`"
          :icon="'card-heading'"
        ></ButtonSmall>
        <template v-if="allows_crud">
          <ButtonSmall
            v-can="'teaching.change_instructionalmethod'"
            :tooltip_text="
              $t('reusable.common.editTooltip', {
                entity: $t('teaching.instructionalMethod.methodEntity'),
              })
            "
            :click_button="
              () =>
                $bvModal.show(`edit-instructional-method-modal-${row.item.id}`)
            "
            :class="`mr-1`"
            :icon="'square'"
          ></ButtonSmall>
          <ButtonSmall
            v-can="'teaching.delete_instructionalmethod'"
            :tooltip_text="
              $t('reusable.common.deleteTooltip', {
                entity: $t('teaching.instructionalMethod.methodEntity'),
              })
            "
            :click_button="() => askForDeleteMethod(row.item)"
            :icon="'trash'"
          ></ButtonSmall>
          <b-modal
            :id="`edit-instructional-method-modal-${row.item.id}`"
            :title="
              $t('reusable.common.editModalTitle', {
                entity: $t('teaching.instructionalMethod.methodEntity'),
              })
            "
            size="lg"
            hide-footer
            ignore-enforce-focus-selector="#sidebar-right"
          >
            <InstructionalMethodForm
              :InstructionalMethod="row.item"
              @saved="
                () =>
                  $bvModal.hide(
                    `edit-instructional-method-modal-${row.item.id}`
                  )
              "
            ></InstructionalMethodForm>
          </b-modal>
        </template>
      </template>
    </GenericBTable>
    <div v-else class="mt-3">
      <strong>{{ $t("teaching.instructionalMethod.methodsEmpty") }}</strong>
    </div>
      </b-tab>

      <b-tab :title="$t('teaching.instructionalMethod.tabStatuses')">
        <InstructionalStatusTable
          :allows_crud="allows_crud"
        ></InstructionalStatusTable>
      </b-tab>
    </b-tabs>

    <!-- Crear método -->
    <b-modal
      id="new-instructional-method-modal"
      :title="
        $t('reusable.common.createModalTitle', {
          entity: $t('teaching.instructionalMethod.methodEntity'),
        })
      "
      size="lg"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
    >
      <InstructionalMethodForm
        @saved="() => $bvModal.hide('new-instructional-method-modal')"
      ></InstructionalMethodForm>
    </b-modal>

    <!-- Momentos del método seleccionado -->
    <b-modal
      id="instructional-moments-modal"
      :title="
        $t('teaching.instructionalMethod.momentsModalTitle', {
          method: selectedMethod ? selectedMethod.name : '',
        })
      "
      size="xl"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
      @hidden="selected_method_id = null"
    >
      <div v-if="selectedMethod">
        <p class="kl-hint mb-2">
          {{ $t("teaching.instructionalMethod.momentsHelp") }}
        </p>
        <div style="text-align: left">
          <ButtonMedium
            v-can="'teaching.add_instructionalmoment'"
            v-if="allows_crud"
            :tooltip_text="
              $t('reusable.common.addTooltip', {
                entity: $t('teaching.instructionalMethod.momentEntity'),
              })
            "
            :text_button="$t('reusable.common.add')"
            :click_button="() => openMomentForm(null)"
            :icon="'plus'"
            :variant="'primary'"
          >
          </ButtonMedium>
        </div>
        <table
          v-if="selectedMethodMoments.length > 0"
          class="table table-sm table-bordered mt-2 mb-0"
        >
          <thead>
            <tr>
              <th class="text-center" style="width: 5rem">
                {{ $t("teaching.instructionalMethod.fieldOrder") }}
              </th>
              <th class="text-center" style="width: 5rem">
                {{ $t("teaching.instructionalMethod.fieldColor") }}
              </th>
              <th>{{ $t("teaching.instructionalMethod.fieldName") }}</th>
              <th>{{ $t("teaching.instructionalMethod.fieldDescription") }}</th>
              <th v-if="allows_crud" style="width: 6rem"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="moment in selectedMethodMoments" :key="moment.id">
              <td class="text-center">{{ moment.order }}</td>
              <td class="text-center">
                <span
                  class="moment-color"
                  :style="{ backgroundColor: moment.color || '#6c757d' }"
                ></span>
              </td>
              <td>{{ moment.name }}</td>
              <td>{{ moment.description }}</td>
              <td v-if="allows_crud" class="text-center">
                <ButtonSmall
                  v-can="'teaching.change_instructionalmoment'"
                  :tooltip_text="
                    $t('reusable.common.editTooltip', {
                      entity: $t('teaching.instructionalMethod.momentEntity'),
                    })
                  "
                  :click_button="() => openMomentForm(moment)"
                  :class="`mr-1`"
                  :icon="'square'"
                ></ButtonSmall>
                <ButtonSmall
                  v-can="'teaching.delete_instructionalmoment'"
                  :tooltip_text="
                    $t('reusable.common.deleteTooltip', {
                      entity: $t('teaching.instructionalMethod.momentEntity'),
                    })
                  "
                  :click_button="() => askForDeleteMoment(moment)"
                  :icon="'trash'"
                ></ButtonSmall>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="mt-3">
          <strong>{{ $t("teaching.instructionalMethod.momentsEmpty") }}</strong>
        </div>
      </div>
    </b-modal>

    <!-- Crear / editar momento -->
    <b-modal
      id="instructional-moment-form-modal"
      :title="
        selected_moment
          ? $t('reusable.common.editModalTitle', {
              entity: $t('teaching.instructionalMethod.momentEntity'),
            })
          : $t('reusable.common.createModalTitle', {
              entity: $t('teaching.instructionalMethod.momentEntity'),
            })
      "
      size="lg"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
    >
      <InstructionalMomentForm
        v-if="selectedMethod"
        :key="moment_form_key"
        :InstructionalMoment="selected_moment"
        :instructional_method_id="selectedMethod.id"
        :next_order="selectedMethodMoments.length + 1"
        @saved="onMomentSaved"
      ></InstructionalMomentForm>
    </b-modal>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "InstructionalMethodView",
  components: {
    GenericBTable: () => import("@/components/reusable/GenericBTable"),
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
    InstructionalMethodForm: () => import("./InstructionalMethodForm"),
    InstructionalStatusTable: () => import("./InstructionalStatusTable"),
    InstructionalMomentForm: () => import("./InstructionalMomentForm"),
  },
  props: {
    display_id: {},
  },
  data() {
    return {
      sortBy: "",
      sortDesc: false,
      input_search: "",
      selected_method_id: null,
      selected_moment: null,
      // Remonta el formulario del momento en cada apertura, para descartar
      // ediciones no guardadas de una apertura previa.
      moment_form_key: 0,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      institution: "getInstitution",
      instructional_methods: names.INSTRUCTIONAL_METHODS,
      instructional_moments: names.INSTRUCTIONAL_MOMENTS,
    }),
    breadcrumb_items() {
      return [
        { text: this.$t("reusable.breadcrumb.home"), to: "/home" },
        { text: this.$t("teaching.instructionalMethod.designTitle") },
      ];
    },
    allows_crud() {
      return this.$hasPermissions("teaching.add_instructionalmethod");
    },
    selectedMethod() {
      if (this.selected_method_id == null) return null;
      return (
        this.instructional_methods.find(
          (x) => x.id == this.selected_method_id
        ) || null
      );
    },
    selectedMethodMoments() {
      if (this.selected_method_id == null) return [];
      return this.momentsByMethod(this.selected_method_id);
    },
    methodsFields() {
      return [
        {
          key: "name",
          label: this.$t("teaching.instructionalMethod.fieldName"),
          sortable: true,
          tdClass: "text-left",
          display_column: true,
        },
        {
          key: "description",
          label: this.$t("teaching.instructionalMethod.fieldDescription"),
          sortable: false,
          tdClass: "text-left",
          display_column: true,
        },
        {
          key: "moments",
          label: this.$t("teaching.instructionalMethod.fieldMoments"),
          sortable: false,
          tdClass: "text-left",
          display_column: true,
        },
        {
          key: "actions",
          label: "",
          display_column: true,
          thClass: "noprint",
          tdClass: "noprint text-center",
        },
      ];
    },
  },
  methods: {
    momentsByMethod(method_id) {
      return this.instructional_moments.filter((x) => x.method == method_id);
    },
    // Mismo criterio que la tarjeta de actividad: el texto se aclara u oscurece según
    // el color del momento, para que el chip se lea con cualquier fondo.
    momentChipStyle(moment) {
      const background_color = moment_color_or_default(moment);
      return {
        backgroundColor: background_color,
        color: this.$lightOrDark(background_color) ? "#2b2a2a" : "#ffffff",
      };
    },
    filterCustom(row) {
      return (
        this.$filtered(String(row.name), this.input_search) ||
        this.$filtered(String(row.description || ""), this.input_search)
      );
    },
    changeInputSearch(input_search) {
      this.input_search = input_search;
    },
    openMomentsModal(method) {
      this.selected_method_id = method.id;
      this.$bvModal.show("instructional-moments-modal");
    },
    openMomentForm(moment) {
      this.selected_moment = moment;
      this.moment_form_key += 1;
      this.$bvModal.show("instructional-moment-form-modal");
    },
    onMomentSaved() {
      this.$bvModal.hide("instructional-moment-form-modal");
      this.selected_moment = null;
    },
    askForDeleteMethod(method) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle", {
          entity: this.$t("teaching.instructionalMethod.methodEntity"),
        }),
        text: this.$t("teaching.instructionalMethod.deleteMethodWarning"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch(names.DELETE_INSTRUCTIONAL_METHOD, method.id)
            .then(() => {
              // Los momentos del método se van en cascada en el backend.
              this.momentsByMethod(method.id).forEach((moment) => {
                this.$store.commit(
                  names.MUTATE_DELETE_INSTRUCTIONAL_MOMENT,
                  moment.id
                );
              });
              toast(
                String(
                  this.$t("teaching.instructionalMethod.methodEntity") +
                    " " +
                    this.$t("reusable.common.deleted")
                )
              );
            })
            .catch(() => {
              toast(this.$t("teaching.instructionalMethod.deleteError"));
            });
        }
      });
    },
    askForDeleteMoment(moment) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle", {
          entity: this.$t("teaching.instructionalMethod.momentEntity"),
        }),
        text: this.$t("teaching.instructionalMethod.deleteMomentWarning"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch(names.DELETE_INSTRUCTIONAL_MOMENT, moment.id)
            .then(() => {
              toast(
                String(
                  this.$t("teaching.instructionalMethod.momentEntity") +
                    " " +
                    this.$t("reusable.common.deleted")
                )
              );
            })
            .catch(() => {
              toast(this.$t("teaching.instructionalMethod.deleteError"));
            });
        }
      });
    },
  },
  watch: {
    // La opción está limitada a KLEARNING (y superusuarios) mientras no se
    // libere al resto de las instituciones: la ruta también se protege.
    institution: {
      immediate: true,
      handler(val) {
        if (!val || !this.user) return;
        if (!this.user.is_superuser && val.internal_use_id != "klearning.cl")
          this.$router.push({ name: "Page403" });
      },
    },
  },
  created() {
    this.$store
      .dispatch(names.FETCH_INSTRUCTIONAL_METHODS, true)
      .catch(() => toast(this.$t("teaching.instructionalMethod.loadError")));
    this.$store
      .dispatch(names.FETCH_INSTRUCTIONAL_MOMENTS, { force: true })
      .catch(() => toast(this.$t("teaching.instructionalMethod.loadError")));
  },
};

// El helper de lightOrDark rompe con un string vacío, así que el color se
// normaliza antes de consultarlo.
function moment_color_or_default(moment) {
  if (
    moment &&
    moment.color &&
    /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(moment.color)
  )
    return moment.color;
  return "#6c757d";
}
</script>

<style scoped>
.kl-hint {
  font-size: var(--kl-fs-small);
  color: #6b7b79;
  margin: 0;
}
/* El color de texto lo decide $lightOrDark en momentChipStyle. */
.moment-chip {
  display: inline-block;
  border-radius: var(--kl-radius-sm, 4px);
  padding: 1px 6px;
  margin: 1px 2px;
  font-size: var(--kl-fs-caption);
}
.moment-color {
  display: inline-block;
  width: 1.1rem;
  height: 1.1rem;
  border-radius: var(--kl-radius-sm, 4px);
  border: 1px solid var(--kl-table-border, #d9e0e0);
}
</style>
